import { useMemo } from 'react';

import { ActivityType } from 'src/generated/mloop-graphql';

import { useAgentDebugLogs } from 'src/contexts/AgentDebugLogsContext';

import { ActivityLogLike } from 'src/entities/ActivityLog/types';

export type ActivityLogOrder = 'NEWEST_FIRST' | 'OLDEST_FIRST';

const getChronologicallyNewestLog = (logs: ActivityLogLike[], order: ActivityLogOrder) => {
  return order === 'NEWEST_FIRST' ? logs[0] : logs[logs.length - 1];
};

/**
 * Filters activity logs for display:
 * - Hides AGENT_DEBUG logs unless the DevTools toggle is enabled
 * - Handles ephemeral logs: only shows the most recent ephemeral log, and only
 *   while it's the newest log overall (agent is actively working). Once a
 *   non-ephemeral log arrives, all ephemeral logs are removed.
 *
 * Returns the filtered logs in caller-provided order and whether the newest log
 * is ephemeral (for shimmer rendering).
 */
export function useFilteredActivityLogs(activityLogs: ActivityLogLike[], activityLogOrder: ActivityLogOrder) {
  const { showDebugLogs } = useAgentDebugLogs();

  const filteredLogs = useMemo(() => {
    let logs = showDebugLogs
      ? activityLogs
      : activityLogs.filter((log) => log.activityType !== ActivityType.AgentDebug);

    // If the newest log is ephemeral, keep only that one.
    // If the newest log is NOT ephemeral, remove all ephemeral logs.
    const newestLog = getChronologicallyNewestLog(logs, activityLogOrder);
    const newestEphemeralId = newestLog?.ephemeral ? newestLog.id : null;

    if (newestEphemeralId) {
      logs = logs.filter((log) => !log.ephemeral || log.id === newestEphemeralId);
    } else {
      logs = logs.filter((log) => !log.ephemeral);
    }

    return logs;
  }, [activityLogOrder, activityLogs, showDebugLogs]);

  const isShimmeringId = useMemo(() => {
    if (filteredLogs.length === 0) {
      return null;
    }
    const newest = getChronologicallyNewestLog(filteredLogs, activityLogOrder);
    return newest?.ephemeral ? newest.id : null;
  }, [activityLogOrder, filteredLogs]);

  return { filteredLogs, isShimmeringId };
}
